import { websiteLocale } from '@formosa/shared/constants/websiteLanguages';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  LOCALE_CHANGE_EVENT,
  LOCALE_STORAGE_KEY,
  getStoredLocale,
} from '@formosa/shared/constants/locale';
import { DEFAULT_LOCALE, translate } from '@formosa/shared/i18n/translate';

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(() => websiteLocale(getStoredLocale() || DEFAULT_LOCALE));

  useEffect(() => {
    function syncLocale(event) {
      const next = event?.detail ?? getStoredLocale();
      setLocale(websiteLocale(next || DEFAULT_LOCALE));
    }

    function onStorage(event) {
      if (event.key === LOCALE_STORAGE_KEY) {
        syncLocale({ detail: event.newValue || '' });
      }
    }

    window.addEventListener(LOCALE_CHANGE_EVENT, syncLocale);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(LOCALE_CHANGE_EVENT, syncLocale);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const t = useCallback((key) => translate(locale, key), [locale]);

  const value = useMemo(() => ({ locale, t }), [locale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within LocaleProvider');
  }
  return ctx;
}
